import { AxiosResponse } from 'axios'
import React from 'react'
import { useAppSelector } from '../../redux/hooks'
import { useDispatch } from 'react-redux'
import casinoService from '../../services/casino.service'
import {
  selectCasinoMatchList,
} from '../../redux/actions/casino/casinoSlice'

import { useParams, useSearchParams } from 'react-router-dom'
import { CustomLink, useNavigateCustom } from '../_layout/elements/custom-link'
import { casinoCategoryIconClass } from './casinoCategoryIcons'
import CasinoListItemInt from '../CasinoList/CasinoListItemInt'
import GameAlertBanner from '../../components/GameAlertBanner'
import authService from '../../services/auth.service'
import { isMobile } from 'react-device-detect'
import { selectUserData, logout } from '../../redux/actions/login/loginSlice'
import User from '../../models/User'

const CasinoOther = () => {
  const dispatch = useDispatch()
  const navigate = useNavigateCustom()
  const gamesList = useAppSelector<any>(selectCasinoMatchList)
  const userState = useAppSelector<{ user: User }>(selectUserData)
  const [searchParams] = useSearchParams();
  const queryp = useParams();
  const category = searchParams.get("category")
  const slotCategoryParam = searchParams.get("slotCategory")
  const [games, setGames] = React.useState<any>([])
  const [provider, setProvider] = React.useState<any>([])
  const [categorylist, setCategoryList] = React.useState<any>([])
  const [gamePlay, setGamePlay] = React.useState<any>()
  const [gameUrl, setGameUrl] = React.useState<string>('')
  const [catName, setCatName] = React.useState<string>('')
  const [allGames, setAllGames] = React.useState<any>([])  // ✅ NEW: Store all games
  const [searchQuery, setSearchQuery] = React.useState('')
  const providerTabsRef = React.useRef<HTMLUListElement>(null)

  const gamesMatchingSearch = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return games
    return games.filter(
      (game: any) =>
        (game.game_name && String(game.game_name).toLowerCase().includes(q)) ||
        (game.identifier && String(game.identifier).toLowerCase().includes(q))
    )
  }, [games, searchQuery])

  React.useEffect(() => {
      casinoService.getIntCasinoList(category, queryp.type).then((res: AxiosResponse<any>) => {
        ///dispatch(setHomePageCasinoMatch(res.data.data))
        const data = res.data
        
        // ✅ Check if this is a silent logout (401 handled by interceptor)
        if (data.silentLogout) {
          console.log('🔴 Silent logout in progress - skipping games update')
          return
        }
        
        setAllGames(data.result)  // ✅ FIX: Store all games
        setGames(data.result)  // Initially show all games
        setProvider(data.providers)
        setCategoryList(data.category)
        const categories = data.category || []
        const fromUrl = slotCategoryParam
          ? categories.find((c: any) => c._id === slotCategoryParam)?._id
          : null
        setCatName(fromUrl || categories?.[0]?._id || "")
      }).catch((error: any) => {
        // ✅ CRITICAL: Suppress ALL error messages (401 already handled by interceptor)
        console.log('Casino list error caught (already handled by interceptor):', error.response?.status)
        // Don't show any toast or alert - interceptor handles it
      })
  }, [category, queryp.type, slotCategoryParam])

  /** Center the active provider tab in the horizontal strip (e.g. after deep-link from dashboard). */
  React.useEffect(() => {
    const ul = providerTabsRef.current
    if (!ul || provider.length === 0) return

    const id = window.requestAnimationFrame(() => {
      const active = ul.querySelector('a.nav-link.active') as HTMLElement | null
      const li = active?.closest('li') as HTMLElement | null
      if (!li) return
      try {
        li.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' })
      } catch {
        const center = li.offsetLeft + li.offsetWidth / 2
        const nextScroll = center - ul.clientWidth / 2
        const maxScroll = Math.max(0, ul.scrollWidth - ul.clientWidth)
        ul.scrollTo({ left: Math.max(0, Math.min(nextScroll, maxScroll)), behavior: 'smooth' })
      }
    })

    return () => cancelAnimationFrame(id)
  }, [category, provider, queryp.type])

  // ✅ NEW: Filter games when category selection changes
  React.useEffect(() => {
    if (catName && allGames.length > 0) {
      // Filter games by selected slot_category
      const filtered = allGames.filter((game: any) => game.slot_category === catName)
      setGames(filtered)
      console.log(`🎰 Filtered games for category "${catName}":`, filtered.length, 'games')
    } else if (allGames.length > 0) {
      // No category selected - show all games
      setGames(allGames)
    }
  }, [catName, allGames])

  /** Deep link from dashboard favorites: `/casino-int/slots?play=<game_identifier>` */
  const playQueryId = searchParams.get('play')
  const consumedPlayQueryRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (!playQueryId) {
      consumedPlayQueryRef.current = null
      return
    }
    if (allGames.length === 0) return
    if (consumedPlayQueryRef.current === playQueryId) return
    const found = allGames.find(
      (g: any) => String(g.game_identifier || g.identifier || '') === playQueryId
    )
    if (found) {
      consumedPlayQueryRef.current = playQueryId
      setGamePlay(found)
    }
  }, [allGames, playQueryId])

  React.useEffect(() => {
    if (gamePlay) {
      const gameId = gamePlay.game_identifier || gamePlay.identifier
      if (!gameId) {
        const { toast } = require('react-toastify')
        toast.warn('This game cannot be launched. Missing game identifier.')
        setGamePlay(undefined)
        return
      }
      const payload: any = {
        gameCode: gameId,
        lobby_url: gameId,
        ipAddress: authService.getIpAddress(),
        isMobile: isMobile,
      }
      casinoService.getplaycasino(payload).then((res: AxiosResponse<any>) => {
        setGameUrl(res.data.data.url)
      }).catch((error: any) => {
        // ✅ FIX: Handle all errors (401, 500, etc.) - show simple "game not available" message
        const { toast } = require('react-toastify')
        
        // Parse the error message to show specific game unavailability reason
        let errorMessage = 'This game is currently not available, please try again later'
        
        try {
          const responseMsg = error.response?.data?.message || ''
          if (/currently locked/i.test(responseMsg)) {
            errorMessage = 'Betting is currently locked for this market by the administrator.'
          } else if (responseMsg.includes('game didn\'t start') || responseMsg.includes('Start time')) {
            errorMessage = 'This game is not available at this time. Please check the game schedule.'
          } else if (responseMsg.includes('maintenance') || responseMsg.includes('Maintenance')) {
            errorMessage = 'This game is under maintenance. Please try again later.'
          } else if (error.response?.status === 500) {
            errorMessage = 'This game is temporarily unavailable. Please try another game.'
          }
        } catch (e) {
          // Use default message
        }
        
        toast.warn(errorMessage, {
          position: 'top-center',
          autoClose: 4000,
        })
        setGamePlay(undefined)  // Close game modal
        console.log('🎮 Casino game error:', error.response?.status || 'Network error', '- showing user-friendly message')
      })
    }
  }, [gamePlay])
  
  // ✅ CRITICAL: Real-time session validation for casino games and game browsing
  React.useEffect(() => {
    if (!userState.user._id) return // Only validate if user is logged in
    
    const myUsername = userState.user.username
    const mySessionId = userState.user.sessionId?.toString()
    
    // BroadcastChannel listener for NEW_LOGIN events
    const channel = new BroadcastChannel('session-channel')
    
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'NEW_LOGIN') {
        const newUsername = event.data.username
        const newSessionId = event.data.sessionId?.toString()
        
        // If SAME account logged in elsewhere with different sessionId
        if (newUsername === myUsername && newSessionId && mySessionId && newSessionId !== mySessionId) {
          console.log('🔴 Casino game: Session conflict detected!')
          console.log('   → Closing game and logging out')
          
          // Close the game iframe if active
          if (gamePlay) {
            setGamePlay(undefined)
            setGameUrl('')
          }
          
          // Logout and redirect (silently, no alert)
          dispatch(logout())
          localStorage.removeItem('token')
          localStorage.removeItem('refreshToken')
          localStorage.removeItem('login-session')
          
          // Silent redirect to login
          window.location.href = '/login'
        }
      }
    }
    
    channel.addEventListener('message', handleMessage)
    
    // Periodic check (every 3 seconds for casino games - more frequent)
    const intervalId = setInterval(() => {
      const sessionData = localStorage.getItem('login-session')
      
      if (sessionData) {
        const [storedUsername, storedSessionId] = sessionData.split(':')
        
        // Check if SAME account but different sessionId
        if (storedUsername === myUsername && storedSessionId !== mySessionId) {
          console.log('🔴 Casino game: Periodic check detected session conflict!')
          console.log('   → Closing game and logging out')
          
          // Close the game iframe if active
          if (gamePlay) {
            setGamePlay(undefined)
            setGameUrl('')
          }
          
          // Logout and redirect (silently, no alert)
          dispatch(logout())
          localStorage.removeItem('token')
          localStorage.removeItem('refreshToken')
          localStorage.removeItem('login-session')
          
          // Silent redirect to login
          window.location.href = '/login'
        }
      }
    }, 3000) // Check every 3 seconds while game is active
    
    return () => {
      channel.removeEventListener('message', handleMessage)
      channel.close()
      clearInterval(intervalId)
    }
  }, [userState.user._id, userState.user.username, userState.user.sessionId, gamePlay, dispatch])
  
  return (
    <>
      <div className='container-fluid container-fluid-5 cas-in-list'>
        <div className='row row5'>
          <div className='col-12 casino-int-layout' style={{ marginTop: '5px' }}>
            <header className='casino-int-banner'>
              <div className='casino-int-banner__title'>
                <i className='fas fa-cube casino-int-banner__icon' aria-hidden />
                <span>CASINO</span>
              </div>
              <form
                className='casino-int-banner__search'
                role='search'
                onSubmit={(e) => e.preventDefault()}
              >
                <input
                  type='search'
                  className='casino-int-banner__input'
                  placeholder='Search Game...'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label='Search games'
                />
                <button type='submit' className='casino-int-banner__search-btn' aria-label='Search'>
                  <i className='fas fa-search' aria-hidden />
                </button>
              </form>
            </header>

            <div className='casino-int-strip casino-int-strip--providers'>
              <ul
                id='casino-provider-tabs'
                ref={providerTabsRef}
                className='nav nav-pills casino-int-tab-row casino-int-tab-row--text'
              >
                {provider.map((Item: any, key: number) => (
                  <li className='nav-item' key={key}>
                    <CustomLink
                      className={`nav-link ${Item._id === category || (!category && key === 0) ? 'active' : ''}`}
                      to={`/casino-int/${queryp.type}?category=${Item._id}`}
                    >
                      <span>{Item._id}</span>
                    </CustomLink>
                  </li>
                ))}
              </ul>
            </div>

            <div className='casino-int-strip casino-int-strip--categories'>
              <ul
                className='nav nav-pills casino-int-tab-row casino-int-tab-row--text casino-int-tab-row--icons'
                id='casino-sub-tab'
              >
                {categorylist?.map((Item: any, key: number) => (
                  <li className='nav-item' key={key}>
                    <button
                      type='button'
                      className={`nav-link ${catName === Item._id ? 'active' : ''}`}
                      onClick={() => setCatName(Item._id)}
                    >
                      <span className='casino-int-tab-row__icon' aria-hidden>
                        <i className={`fas ${casinoCategoryIconClass(Item._id)}`} />
                      </span>
                      <span className='casino-int-tab-row__label'>{Item._id}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className='casino-list casino-int-games-grid mt-2'>
              {games.length > 0 && gamesMatchingSearch.length === 0 && (
                <p className='casino-int-empty'>No games match your search.</p>
              )}
              {gamesMatchingSearch.length > 0 && (
                <CasinoListItemInt games={gamesMatchingSearch} setGamePlay={setGamePlay} />
              )}
            </div>
          </div>
        </div>
      </div>
      <GameAlertBanner gamePlay={gameUrl && gamePlay ? gamePlay : null} />
      {gamePlay?.game_name && gameUrl && (
        <div className='slot-iframe show'>
        <div className="slot-header">
          <div className="title"><h4 className='mb-0' style={{fontSize:"20px", fontWeight:"400"}}>{gamePlay?.game_name}</h4></div>
          <div className="close-slot-frame" onClick={()=>setGamePlay(undefined)}>EXIT</div>
          </div>
          <iframe
            scrolling='no'
            allow='fullscreen;'
            src={gameUrl}
            title={gamePlay?.game_name}
            style={{ width: '100%', border: '0px' }}
          />
        </div>
      )}
    </>
  )
}
export default CasinoOther
